import React from "react";

import type { ISession } from "@mne-authentication/api";

import type { Palette, PaletteColor } from "@mui/material/styles";

import type { IntlShape } from "@mnemonic/intl";
import type { TArgusLocale } from "@mnemonic/mui";
import { CoreMuiProvider, createArgusTheme } from "@mnemonic/mui";

interface ICustomPalette extends Palette {
  tertiary: Array<PaletteColor>;
  criticality?: { low: PaletteColor; medium: PaletteColor; high: PaletteColor; critical: PaletteColor };
  getTertiaryColor: (index: number) => PaletteColor;
  getCriticalityColor: (
    criticality: number | "low" | "medium" | "high" | "critical" | "LOW" | "MEDIUM" | "HIGH" | "CRITICAL",
  ) => PaletteColor;
}

//This is necessary to override the default theme with our custom values and make the the theme available without needing to type it everywhere.
declare module "@mui/material/styles" {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Theme {
    palette: ICustomPalette;
  }
}

export interface IThemeProviderProps {
  children: React.ReactNode;
  intl: IntlShape;
  session: ISession;
}

export default function ThemeProvider({ children, intl, session }: IThemeProviderProps) {
  /* Disabling dark mode in swagger until we have a solution that looks better */
  const sessionWithLightMode: ISession = {
    ...session,
    user: { ...session?.user, properties: { ...session?.user?.properties, "ui.themeMode": "light" } },
  };

  const theme = createArgusTheme(sessionWithLightMode, (createTheme, localizationOpts) =>
    createTheme(
      // Pass a empty object if there are no local overrides
      {},
      ...localizationOpts,
    ),
  );
  return (
    <CoreMuiProvider locale={intl?.locale as TArgusLocale} domain={session?.user?.domain} theme={theme}>
      {children}
    </CoreMuiProvider>
  );
}
